import React, { useState, useEffect } from 'react';
import { X, Plus, Database } from 'lucide-react';
import Button from '../ui/Button';

interface AddNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (nodeData: NewNodeData) => void;
  existingNodes: string[];
}

export interface NewNodeData {
  nodeLabel: string;
  category: string;
  description: string;
  properties: { name: string; type: string; isPrimaryKey: boolean }[];
}

const CATEGORY_OPTIONS = [
  { value: 'Sales', label: 'Sales', color: '#f59e0b' },
  { value: 'Inventory', label: 'Inventory', color: '#10b981' },
  { value: 'Finance', label: 'Finance', color: '#8b5cf6' },
  { value: 'HR', label: 'HR', color: '#ec4899' },
  { value: 'Production', label: 'Production', color: '#f97316' },
  { value: 'Logistics', label: 'Logistics', color: '#6366f1' },
  { value: 'Customer', label: 'Customer', color: '#06b6d4' },
  { value: 'Product Hub', label: 'Product Hub', color: '#0ea5e9' },
  { value: 'Supplier Hub', label: 'Supplier Hub', color: '#06b6d4' },
  { value: 'Order-to-Cash', label: 'Order-to-Cash', color: '#f59e0b' },
  { value: 'Procure-to-Pay', label: 'Procure-to-Pay', color: '#f97316' },
  { value: 'Other', label: 'Other', color: '#64748b' },
];

const DATA_TYPE_OPTIONS = [
  'String',
  'Integer',
  'Float',
  'Boolean',
  'Date',
  'DateTime',
  'UUID',
];

export const AddNodeModal: React.FC<AddNodeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  existingNodes,
}) => {
  const [nodeLabel, setNodeLabel] = useState('');
  const [category, setCategory] = useState('Other');
  const [description, setDescription] = useState('');
  const [properties, setProperties] = useState<
    { name: string; type: string; isPrimaryKey: boolean }[]
  >([{ name: 'id', type: 'UUID', isPrimaryKey: true }]);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNodeLabel('');
      setCategory('Other');
      setDescription('');
      setProperties([{ name: 'id', type: 'UUID', isPrimaryKey: true }]);
      setError('');
    }
  }, [isOpen]);

  const handleAddProperty = () => {
    setProperties([
      ...properties,
      { name: '', type: 'String', isPrimaryKey: false },
    ]);
  };

  const handleRemoveProperty = (index: number) => {
    setProperties(properties.filter((_, i) => i !== index));
  };

  const handlePropertyChange = (
    index: number,
    field: 'name' | 'type' | 'isPrimaryKey',
    value: string | boolean
  ) => {
    const updated = [...properties];
    if (field === 'isPrimaryKey') {
      // Only one primary key allowed
      if (value === true) {
        updated.forEach((p, i) => (p.isPrimaryKey = i === index));
      }
    } else {
      (updated[index] as any)[field] = value;
    }
    setProperties(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedLabel = nodeLabel.trim();

    if (!trimmedLabel) {
      setError('Node label is required');
      return;
    }

    // Validate label format (PascalCase recommended)
    if (!/^[A-Z][a-zA-Z0-9]*$/.test(trimmedLabel)) {
      setError('Node label should be PascalCase (e.g., Customer, OrderItem)');
      return;
    }

    // Check for duplicates
    if (existingNodes.includes(trimmedLabel)) {
      setError('A node with this label already exists');
      return;
    }

    // Validate properties
    const validProperties = properties.filter((p) => p.name.trim());
    if (validProperties.length === 0) {
      setError('At least one property is required');
      return;
    }

    onConfirm({
      nodeLabel: trimmedLabel,
      category,
      description: description || `${trimmedLabel} entity`,
      properties: validProperties,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                Add New Node
              </h2>
              <p className="text-sm text-neutral-500">
                Create a new entity in the schema
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Node Label */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Node Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nodeLabel}
                onChange={(e) => {
                  setNodeLabel(e.target.value);
                  setError('');
                }}
                placeholder="e.g., Customer, OrderItem, Product"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              <p className="text-xs text-neutral-400">
                Use PascalCase (e.g., Customer, OrderItem)
              </p>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Category / KG Area
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">
                Description <span className="text-neutral-400">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this entity..."
                rows={2}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Properties */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-neutral-700">
                  Properties
                </label>
                <button
                  type="button"
                  onClick={handleAddProperty}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Property
                </button>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto border border-neutral-200 rounded-lg p-2">
                {properties.map((prop, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-neutral-50 rounded"
                  >
                    <input
                      type="text"
                      value={prop.name}
                      onChange={(e) =>
                        handlePropertyChange(index, 'name', e.target.value)
                      }
                      placeholder="Property name"
                      className="flex-1 px-2 py-1 text-sm border border-neutral-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                    <select
                      value={prop.type}
                      onChange={(e) =>
                        handlePropertyChange(index, 'type', e.target.value)
                      }
                      className="px-2 py-1 text-sm border border-neutral-300 rounded focus:ring-1 focus:ring-blue-500"
                    >
                      {DATA_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-neutral-500">
                      <input
                        type="checkbox"
                        checked={prop.isPrimaryKey}
                        onChange={(e) =>
                          handlePropertyChange(
                            index,
                            'isPrimaryKey',
                            e.target.checked
                          )
                        }
                        className="w-3 h-3"
                      />
                      PK
                    </label>
                    {properties.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveProperty(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Node
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNodeModal;
